'use client'

import {
  Popover as NextUIPopover,
  PopoverContent as NextUIPopoverContent,
  PopoverTrigger as NextUIPopoverTrigger
} from '@nextui-org/react'
import * as React from 'react'
import { cm } from '~/utils/utils'

export interface PopoverProps {
  /** Whether the popover is open (controlled) */
  isOpen?: boolean
  /** Default open state (uncontrolled) */
  defaultOpen?: boolean
  /** Callback when open state changes */
  onOpenChange?: (isOpen: boolean) => void
  /** Placement of the popover relative to trigger */
  placement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end'
    | 'right'
    | 'right-start'
    | 'right-end'
  /** Offset from the trigger */
  offset?: number
  /** Whether the popover can flip to the opposite side when space is tight */
  shouldFlip?: boolean
  /** Whether clicking outside closes the popover (default: true) */
  closeOnClickOutside?: boolean
  /** Whether pressing Escape closes the popover (default: true) */
  closeOnEscape?: boolean
  /** Children - should include PopoverTrigger and PopoverContent */
  children: React.ReactNode
}

/** Decide whether NextUI should close the popover for an outside interaction; keeps the popover open when a nested Radix dropdown/menu/listbox is active or any Radix portal is open in the DOM. */
export function shouldClosePopoverOnInteractOutside(
  element: Element,
  opts: { closeOnClickOutside: boolean }
): boolean {
  if (!opts.closeOnClickOutside) return false

  const isInsideRadixDropdown =
    element.closest('[data-radix-popper-content-wrapper]') !== null ||
    element.closest('[data-radix-menu-content]') !== null ||
    element.closest('[role="menu"]') !== null ||
    element.closest('[role="listbox"]') !== null

  if (isInsideRadixDropdown) {
    return false
  }

  const hasOpenRadixDropdown =
    document.querySelector('[data-radix-popper-content-wrapper]') !== null ||
    document.querySelector('[data-radix-menu-content][data-state="open"]') !== null

  if (hasOpenRadixDropdown) {
    return false
  }

  return true
}

/**
 * Popover component - A floating panel that appears on click
 *
 * Automatically closes when clicking outside or pressing Escape.
 *
 * @example
 * ```tsx
 * <Popover>
 *   <PopoverTrigger>
 *     <Button>Open</Button>
 *   </PopoverTrigger>
 *   <PopoverContent>
 *     <div>Popover content here</div>
 *   </PopoverContent>
 * </Popover>
 * ```
 *
 * @example Controlled
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false)
 *
 * <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
 *   <PopoverTrigger>
 *     <Button>Open</Button>
 *   </PopoverTrigger>
 *   <PopoverContent>
 *     <div>Content</div>
 *   </PopoverContent>
 * </Popover>
 * ```
 */
export function Popover({
  isOpen,
  defaultOpen = false,
  onOpenChange,
  placement = 'bottom',
  offset = 8,
  shouldFlip = true,
  closeOnClickOutside = true,
  closeOnEscape = true,
  children
}: PopoverProps) {
  const shouldCloseOnInteractOutside = React.useCallback(
    (element: Element) => shouldClosePopoverOnInteractOutside(element, { closeOnClickOutside }),
    [closeOnClickOutside]
  )

  return (
    <NextUIPopover
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      placement={placement}
      offset={offset}
      shouldFlip={shouldFlip}
      shouldCloseOnBlur={false}
      shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
      isKeyboardDismissDisabled={!closeOnEscape}
      classNames={{
        base: 'p-0',
        content: 'p-0'
      }}
    >
      {children as React.ReactNode[]}
    </NextUIPopover>
  )
}

export interface PopoverTriggerProps {
  /** The trigger element - should be a single element that can receive focus */
  children: React.ReactElement
  /** Additional className for the trigger wrapper */
  className?: string
}

/**
 * PopoverTrigger - Wraps the element that triggers the popover
 *
 * The child must be a single React element that can receive focus and click events.
 */
export function PopoverTrigger({ children, className }: PopoverTriggerProps) {
  return <NextUIPopoverTrigger className={className}>{children}</NextUIPopoverTrigger>
}

export interface PopoverContentProps {
  /** Content to display in the popover */
  children: React.ReactNode
  /** Additional className for styling */
  className?: string
  /** Width of the popover content */
  width?: string | number
}

/**
 * PopoverContent - The content displayed inside the popover
 *
 * Styled with design system tokens for consistent appearance.
 */
export function PopoverContent({ children, className, width }: PopoverContentProps) {
  const widthValue = width ? (typeof width === 'number' ? `${width}px` : width) : undefined

  return (
    <NextUIPopoverContent className='p-0 bg-transparent border-none shadow-none'>
      <div
        className={cm(
          'p-4 bg-white rounded-2xl border border-tokens-neutral-border-2 shadow-popover',
          className
        )}
        style={widthValue ? { width: widthValue } : undefined}
      >
        {children}
      </div>
    </NextUIPopoverContent>
  )
}

// Re-export for convenience
export { Popover as default }
